import { useState, RefObject } from 'react';
import { useSearchParams } from 'react-router-dom';

import ExportIcon from '~/shared/assets/icons/export.svg';
import { ReportSelectionOption, SelectOption } from '~/shared/utils/types';
import ChartFilterDropdown from '~/shared/components/ChartFilterDropdown';
import Button from '~/shared/components/ButtonsV2';
import { LineChartHandle } from '~/shared/components/LineChart/LineChart';
import { useExportState } from '~/shared/context/ExportStateContext';

import { CUSTOM_DATES_TITLE, dateFilterItems } from '../common/dateFilterMenuItems';
import { apiFilterKey } from '../common/serachParamKeys';
import ReportsExportModal from '../ReportsPage/components/ReportsExportModal';

import ReportsFiltersSidePanel from './FiltersSidePanel/ReportsFiltersSidePanel';

interface ReportsFilterSectionProps {
  activeDateFilterTitle: string | null;
  currentBaseFilters: ReportSelectionOption[];
  onDateFilterChange: (filter: {
    title: string;
    duration?: { start: Duration; end?: Duration };
  }) => void;
  onBaseFiltersChange: (newFilters: ReportSelectionOption[]) => void;
  setPageOffset: (offset: string) => void;
  utilizationChartRef: RefObject<LineChartHandle | null>;
  usageChartRef: RefObject<LineChartHandle | null>;
  tableQueryUrl: string;
  tableCountUrl: string;
  chartQueryUrl: string;
  totalCount: number | undefined;
}

export default function ReportsFilterSection({
  activeDateFilterTitle,
  onDateFilterChange,
  setPageOffset,
  utilizationChartRef,
  usageChartRef,
  tableQueryUrl,
  tableCountUrl,
  chartQueryUrl,
  totalCount,
}: ReportsFilterSectionProps) {
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { isExporting } = useExportState();
  const reportsExporting = isExporting('reports');
  const [searchParams, setSearchParams] = useSearchParams();
  const apiFilters = searchParams.get(apiFilterKey);

  const currentBaseFilters = JSON.parse(apiFilters || '[]') as SelectOption[];

  const extractAppliedFilters = () => {
    const processedFilters = searchParams.get('processed_filters');
    const appliedClusterNameFilters: SelectOption[] = [];
    const appliedInstanceFilters: SelectOption[] = [];
    const appliedVolumeIdFilters: SelectOption[] = [];
    const appliedVolumeTagFilters: SelectOption[] = [];

    const TAG_FIELD_RE = /^tag:(.+)$/;
    const VALUE_SPLIT_RE = /\s*,\s*/;

    if (processedFilters) {
      const parsedProcessedFilters = JSON.parse(processedFilters) as string[];

      parsedProcessedFilters.forEach((filterStr) => {
        const filter = JSON.parse(filterStr);
        const { field, value, op } = filter;

        if ((op === 'in' || op === 'equals') && typeof value === 'string') {
          const values = value.split(VALUE_SPLIT_RE);
          const tagMatch = TAG_FIELD_RE.exec(field);

          values.forEach((val: string) => {
            const trimmed = val.trim();
            if (!trimmed) {
              return;
            }

            if (field === 'clusterName') {
              appliedClusterNameFilters.push({ id: trimmed, name: trimmed, group: 'clusterName' });
            } else if (field === 'instanceId') {
              appliedInstanceFilters.push({ id: trimmed, name: trimmed, group: 'instanceId' });
            } else if (field === 'volumeId') {
              appliedVolumeIdFilters.push({ id: trimmed, name: trimmed, group: 'volumeId' });
            } else if (tagMatch) {
              const key = tagMatch[1];
              const name = key && trimmed ? `${key}:${trimmed}` : key || trimmed;
              appliedVolumeTagFilters.push({ id: name, name, group: `tag:${key}` as const });
            }
          });
        }
      });
    } else if (apiFilters) {
      // Fallback to api_filters
      appliedClusterNameFilters.push(
        ...currentBaseFilters.filter((f) => f.group === 'clusterName')
      );
      appliedInstanceFilters.push(...currentBaseFilters.filter((f) => f.group === 'instanceId'));
      appliedVolumeIdFilters.push(...currentBaseFilters.filter((f) => f.group === 'volumeId'));
      appliedVolumeTagFilters.push(
        ...currentBaseFilters.filter(
          (f) => typeof f.group === 'string' && (f.group as string).startsWith('tag:')
        )
      );
    }

    return {
      appliedClusterNameFilters,
      appliedInstanceFilters,
      appliedVolumeIdFilters,
      appliedVolumeTagFilters,
    };
  };

  const {
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
  } = extractAppliedFilters();
  const appliedAutoscaleState = searchParams.get('autoscaling_state')?.split(',') || [];

  // Determine active filters (excluding empty/default if needed)
  const activeFilterCount = currentBaseFilters.length;

  const activeDateFilterSelectedItem: {
    title: string;
    duration?: { start: Duration; end?: Duration };
  } = (() => {
    if (activeDateFilterTitle && activeDateFilterTitle.includes(CUSTOM_DATES_TITLE)) {
      return { title: activeDateFilterTitle };
    }
    const foundItem = dateFilterItems.find((item) => item.title === activeDateFilterTitle);
    return (
      foundItem ||
      dateFilterItems.find((item) => item.title === 'Last 3 hours') ||
      dateFilterItems[0] || { title: 'Error: No Date Filter' }
    );
  })();

  const handleResetFilters = () => {
    setPageOffset('0'); // Reset pagination
    setSearchParams(
      (params) => {
        params.delete(apiFilterKey);
        params.delete('autoscaling_state');
        params.delete('processed_filters');
        return params;
      },
      { replace: true }
    );
    setSidePanelOpen(false);
  };

  return (
    <div className="px-[30px] py-[20px] flex justify-between gap-4 ">
      <ChartFilterDropdown
        align="top-left"
        items={dateFilterItems}
        onSelect={onDateFilterChange}
        selectedItem={activeDateFilterSelectedItem}
      />
      <div className="ml-auto flex gap-2 z-30 items-center">
        <Button
          variant="third"
          onClick={() => setIsExportModalOpen(true)}
          className="w-10"
          size="md-responsive"
          loading={reportsExporting}
          disabled={reportsExporting}
          icon={<ExportIcon className="size-5" />}
        />
        <ReportsFiltersSidePanel
          open={sidePanelOpen}
          onOpenChange={setSidePanelOpen}
          onResetFilters={handleResetFilters}
          appliedClusterNameFilters={appliedClusterNameFilters}
          appliedInstanceFilters={appliedInstanceFilters}
          appliedVolumeIdFilters={appliedVolumeIdFilters}
          appliedVolumeTagFilters={appliedVolumeTagFilters}
          appliedAutoscaleState={appliedAutoscaleState}
          activeFilterCount={activeFilterCount}
          setSidePanelOpen={setSidePanelOpen}
          isSidePanelOpen={sidePanelOpen}
        />
      </div>

      <ReportsExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        activeFilters={currentBaseFilters}
        utilizationChartRef={utilizationChartRef}
        usageChartRef={usageChartRef}
        tableQueryUrl={tableQueryUrl}
        tableCountUrl={tableCountUrl}
        chartQueryUrl={chartQueryUrl}
        activeDateFilterTitle={activeDateFilterTitle}
      />
    </div>
  );
}
